import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export default function GoogleTrendsTest() {
  const [keywordsInput, setKeywordsInput] = useState('amerika turu');
  const [geo, setGeo] = useState('TR');
  const [timeRange, setTimeRange] = useState('now 7-d');
  const [category, setCategory] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bqRising, setBqRising] = useState([]);
  const [bqTop, setBqTop] = useState([]);
  const [bqDailyTop, setBqDailyTop] = useState([]);
  const [topN, setTopN] = useState(10);

  const timeseriesRef = useRef(null);
  const relatedRef = useRef(null);

  const regions = useMemo(() => [
    { code: 'TR', name: 'Turkey' },
    { code: 'US', name: 'United States' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'AE', name: 'United Arab Emirates' },
  ], []);

  const timeRanges = [
    { value: 'now 1-d', label: 'Past 1 day' },
    // Use daily granularity for 7 days instead of hourly
    { value: 'today 7-d', label: 'Past 7 days' },
    { value: 'today 1-m', label: 'Past 30 days' },
    { value: 'today 3-m', label: 'Past 90 days' },
    { value: 'today 12-m', label: 'Past 12 months' },
    { value: 'today+5-y', label: 'Past 5 years' },
  ];

  // Normalize BigQuery row values to primitives for safe rendering
  const unwrapValue = (val) => {
    if (val === null || val === undefined) return val;
    if (typeof val === 'object') {
      if (Object.prototype.hasOwnProperty.call(val, 'value')) {
        return val.value;
      }
      try {
        return JSON.stringify(val);
      } catch {
        return String(val);
      }
    }
    return val;
  };

  const normalizeRows = (rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => {
      const normalized = {};
      Object.entries(row || {}).forEach(([k, v]) => {
        normalized[k] = unwrapValue(v);
      });
      return normalized;
    });
  };

  const loadTrendsScript = useCallback(async () => {
    setError('');
    if (typeof window !== 'undefined' && window.trends && window.trends.embed) return;
    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-trends-embed="1"]');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Failed to load Trends embed script')));
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://ssl.gstatic.com/trends_nrtr/3351_RC01/embed_loader.js';
      s.async = true;
      s.defer = true;
      s.dataset.trendsEmbed = '1';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load Trends embed script'));
      document.body.appendChild(s);
    });
  }, []);

  const renderWidgets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await loadTrendsScript();
      const kw = keywordsInput
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .slice(0, 5); // Trends supports up to 5 comparison terms

      if (kw.length === 0) {
        setError('Please enter at least one keyword.');
        setLoading(false);
        return;
      }

      const comparisonItem = kw.map(k => ({ keyword: k, geo, time: timeRange }));
      const exploreObj = {
        comparisonItem,
        category: Number(category) || 0,
        property: ''
      };

      // Clear containers
      [timeseriesRef.current, relatedRef.current].forEach(node => {
        if (node) node.innerHTML = '';
      });

      // Render Time Series
      window.trends.embed.renderExploreWidgetTo(
        timeseriesRef.current,
        'TIMESERIES',
        exploreObj,
        { exploreQuery: null, guestPath: 'https://trends.google.com:443/trends/embed/' }
      );

      // Render Related Queries
      window.trends.embed.renderExploreWidgetTo(
        relatedRef.current,
        'RELATED_QUERIES',
        exploreObj,
        { exploreQuery: null, guestPath: 'https://trends.google.com:443/trends/embed/' }
      );
    } catch (e) {
      setError(e?.message || 'Failed to render Google Trends');
    } finally {
      setLoading(false);
    }
  }, [category, geo, keywordsInput, loadTrendsScript, timeRange]);

  const fetchRising = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`/api/google-trends/international/top-rising?country=${encodeURIComponent(geo)}&limit=100`, { credentials: 'include' });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed');
      setBqRising(normalizeRows(data.rows || []));
    } catch (e) {
      setError(e?.message || 'Failed to fetch rising terms');
    }
  }, [geo]);

  const fetchTop = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`/api/google-trends/international/top?country=${encodeURIComponent(geo)}&weeks=4&limit=200`, { credentials: 'include' });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed');
      setBqTop(normalizeRows(data.rows || []));
    } catch (e) {
      setError(e?.message || 'Failed to fetch top terms');
    }
  }, [geo]);

  const fetchDailyTop = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`/api/google-trends/international/daily/top?country=${encodeURIComponent(geo)}&limit=${encodeURIComponent(topN || 10)}` , { credentials: 'include' });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed');
      setBqDailyTop(normalizeRows(data.rows || []));
    } catch (e) {
      setError(e?.message || 'Failed to fetch daily top terms');
    }
  }, [geo, topN]);

  useEffect(() => {
    renderWidgets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="rounded-2xl overflow-hidden border shadow-sm">
          <div className="p-6 bg-white text-gray-900 border-b">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <img src="/googletrendslogo.png" alt="Google Trends" className="h-7 w-auto object-contain" />
                <h1 className="text-xl font-semibold">Google Trends — Test</h1>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  value={keywordsInput}
                  onChange={(e) => setKeywordsInput(e.target.value)}
                  placeholder="Keywords (comma separated)"
                  className="px-3 py-2 border rounded-lg text-sm w-64"
                />
                <select value={geo} onChange={(e) => setGeo(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
                  {regions.map(r => (
                    <option key={r.code} value={r.code}>{r.name}</option>
                  ))}
                </select>
                <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
                  {timeRanges.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm w-28"
                  title="Google Trends category ID (0 = All)"
                />
                <button
                  onClick={renderWidgets}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg bg-black text-white text-sm disabled:opacity-50"
                >
                  {loading ? 'Rendering…' : 'Render'}
                </button>
              </div>
            </div>
            {error && (
              <div className="mt-3 text-sm text-red-600">{error}</div>
            )}
          </div>

          <div className="p-6 bg-white">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <div className="text-sm font-medium text-gray-900 mb-2">Interest over time</div>
                <div ref={timeseriesRef} className="w-full min-h-[360px] border rounded-lg overflow-hidden" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900 mb-2">Related queries</div>
                <div ref={relatedRef} className="w-full min-h-[360px] border rounded-lg overflow-hidden" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-gray-900">BigQuery — Top (last 24h)</div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600">Top N</label>
                    <input type="number" min={1} max={1000} value={topN} onChange={(e) => setTopN(Number(e.target.value) || 1)} className="px-2 py-1 border rounded w-16 text-xs" />
                    <button onClick={fetchDailyTop} className="px-3 py-1.5 rounded-lg bg-black text-white text-xs">Fetch 24h</button>
                  </div>
                </div>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">refresh_date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">term</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">total_score</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {bqDailyTop.map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-xs text-gray-500">{r.refresh_date}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{r.term}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{r.total_score}</td>
                        </tr>
                      ))}
                      {bqDailyTop.length === 0 && (
                        <tr><td className="px-3 py-3 text-sm text-gray-400" colSpan={4}>No data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-gray-900">BigQuery — International Rising (last week)</div>
                  <div className="flex items-center gap-2">
                    <button onClick={fetchRising} className="px-3 py-1.5 rounded-lg bg-black text-white text-xs">Fetch Rising</button>
                  </div>
                </div>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">rank</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">term</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">percent_gain</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">score</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">week</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {bqRising.slice(0, 100).map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm text-gray-700">{r.rank}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{r.term}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{r.percent_gain}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{r.score}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{r.week}</td>
                        </tr>
                      ))}
                      {bqRising.length === 0 && (
                        <tr><td className="px-3 py-3 text-sm text-gray-400" colSpan={5}>No data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-gray-900">BigQuery — International Top (last 4 weeks)</div>
                  <div className="flex items-center gap-2">
                    <button onClick={fetchTop} className="px-3 py-1.5 rounded-lg bg-black text-white text-xs">Fetch Top</button>
                  </div>
                </div>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">week</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">rank</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">term</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">score</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {bqTop.slice(0, 200).map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-xs text-gray-500">{r.week}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{r.rank}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{r.term}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{r.score}</td>
                        </tr>
                      ))}
                      {bqTop.length === 0 && (
                        <tr><td className="px-3 py-3 text-sm text-gray-400" colSpan={4}>No data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


